import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IconColor } from '@freelancer/ui/icon';
import {
  LinkColor,
  LinkIconPosition,
  LinkUnderline,
  LinkWeight,
} from '@freelancer/ui/link';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { textVariants } from '@freelancer/ui/text';
import {
  FontColor,
  FontWeight,
  TextAlign,
  TextSize,
} from '@freelancer/ui/text';

@Component({
  standalone: false,
  selector: 'app-navigation-menu-card',
  template: `
    <fl-link
      class="Container"
      [flTrackingLabel]="trackingLabel"
      [underline]="LinkUnderline.NEVER"
      [link]="url"
      [newTab]="false"
    >
      <div class="PictureContainer">
        <fl-picture
          class="Picture"
          [borderRadiusTop]="true"
          [objectFit]="PictureObjectFit.COVER"
          [src]="imgSrc"
          [aspectRatioValue]="'rectangle-small'"
        ></fl-picture>
      </div>
      <div
        class="Info"
        [class.LongDescription]="longDescription"
        [class.IsLoggedOut]="isLoggedOut"
      >
        <p
          [flMaxLines]="2"
          [class]="descriptionClasses"
        >
          {{ description }}
        </p>

        @if (!!subDescription) {
          <p
            [flMaxLines]="2"
            [class]="subDescriptionClasses"
          >
            {{ subDescription }}
          </p>
        }

        @if (readMore) {
          <fl-link
            flTrackingLabel="MenuCardClick"
            i18n="Read more link"
            class="ViewMoreColor"
            [size]="TextSize.BODY_16"
            [iconName]="'ui-arrow-forward'"
            [iconPosition]="LinkIconPosition.RIGHT"
            [weight]="LinkWeight.MEDIUM"
            [color]="LinkColor.INHERIT"
            [iconColor]="IconColor.INHERIT"
            [iconAnimation]="true"
          >
            Find out more
          </fl-link>
        }
      </div>
    </fl-link>
  `,
  styleUrls: ['./navigation-menu-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMenuCardComponent implements OnInit, OnChanges {
  IconColor = IconColor;
  LinkIconPosition = LinkIconPosition;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  PictureObjectFit = PictureObjectFit;
  TextSize = TextSize;
  LinkColor = LinkColor;

  descriptionClasses: string;
  subDescriptionClasses: string;

  @Input() descriptionColor = FontColor.LIGHT;
  @Input() descriptionWeight = FontWeight.MEDIUM;
  @Input() url: string;
  @Input() imgSrc: string;
  @Input() description: string;
  @Input() subDescription: string;
  @Input() trackingLabel = 'MenuCardClick';
  @Input() readMore = false;
  @Input() longDescription = false;
  @Input() isLoggedOut = false;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'descriptionColor' in changes ||
      'descriptionWeight' in changes ||
      'longDescription' in changes
    ) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.descriptionClasses = textVariants({
      color: this.descriptionColor,
      size: TextSize.BODY_16,
      sizeDesktopXLarge: TextSize.BODY_16,
      weight: this.descriptionWeight,
      align: this.longDescription ? TextAlign.LEFT : TextAlign.CENTER,
    });

    this.subDescriptionClasses = textVariants({
      color: this.descriptionColor,
      size: TextSize.BODY_16,
    });
  }
}
